import type { AgentShareProfile } from '../../services/bsideAgentShareService';

// Contact exchange/download is deferred for this release; keep implementation for later.
export const NEXA_CONTACT_ACTIONS_ENABLED = false;

export const NEXA_ME_FONT = "'Montserrat', sans-serif";

export const safePublicUrl = (value?: string | null): string => {
  if (!value?.trim()) return '';
  try {
    const url = new URL(value.trim());
    return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
};

// The current public API has no verified-license result. merchantIdentity and
// the presence of a license number do not establish that an agent is verified.
export const hasVerifiedAgentResult = (profile: AgentShareProfile | null): boolean => profile?.licenseVerified === true;

const escapeVCardText = (value: string): string => value
  .replace(/\\/g, '\\\\')
  .replace(/\r\n|\r|\n/g, '\\n')
  .replace(/;/g, '\\;')
  .replace(/,/g, '\\,');

// Fold UTF-8 content at the vCard 3.0 75-octet boundary, keeping Unicode intact.
const foldVCardLine = (line: string): string => {
  let result = '';
  let bytes = 0;
  for (const character of line) {
    const size = unescape(encodeURIComponent(character)).length;
    if (bytes + size > 75) {
      result += '\r\n ';
      bytes = 1;
    }
    result += character;
    bytes += size;
  }
  return result;
};

export const buildAgentVCard = (profile: AgentShareProfile, publicUrl: string): string => {
  const name = String(profile.nickname || '').trim();
  const values = ['BEGIN:VCARD', 'VERSION:3.0', `FN:${escapeVCardText(name)}`, `N:;${escapeVCardText(name)};;;`];
  const append = (key: string, value?: string | null) => {
    if (value?.trim()) values.push(`${key}:${escapeVCardText(value.trim())}`);
  };
  append('ORG', profile.merchantName);
  append('TEL;TYPE=WORK,VOICE', profile.phone);
  append('EMAIL;TYPE=WORK', profile.email);
  if (profile.merchantAddress?.trim()) values.push(`ADR;TYPE=WORK:;;${escapeVCardText(profile.merchantAddress.trim())};;;;`);
  const website = safePublicUrl(profile.website);
  if (website) append('URL', website);
  append('item1.URL', safePublicUrl(publicUrl));
  append('item1.X-ABLabel', 'Nexa Me');
  append('NOTE', `Saved from ${name}'s Nexa Me.${profile.bio?.trim() ? `\n${profile.bio.trim()}` : ''}`);
  let avatar = safePublicUrl(profile.avatar);
  if (!avatar && profile.avatar?.startsWith('/') && safePublicUrl(publicUrl)) {
    avatar = safePublicUrl(new URL(profile.avatar, publicUrl).href);
  }
  if (avatar) append('PHOTO;VALUE=URI', avatar);
  values.push('END:VCARD');
  return `${values.map(foldVCardLine).join('\r\n')}\r\n`;
};

export const downloadAgentVCard = (profile: AgentShareProfile, publicUrl: string): void => {
  if (!NEXA_CONTACT_ACTIONS_ENABLED) return;
  const url = URL.createObjectURL(new Blob([buildAgentVCard(profile, publicUrl)], { type: 'text/vcard;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${String(profile.nickname || 'agent').replace(/[\\/:*?"<>|]/g, '').trim() || 'agent'}.vcf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const isBookingOnlyMode = (search: string): boolean => new URLSearchParams(search).get('booking') === '1';

export const buildEmbeddedBookingPath = (pathname: string): string => `${pathname}?booking=1`;
